import { styled } from '@mui/material/styles';
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type FocusEventHandler,
  type KeyboardEventHandler,
} from 'react';

function normalizeMinutes(digits: string) {
  if (!digits) {
    return '';
  }
  if (Number(digits[0]) > 5) {
    return `0${digits[0]}`;
  }
  return digits.slice(0, 2);
}

export function formatSmartTimeInput(rawValue: string) {
  const colonIndex = rawValue.indexOf(':');

  if (colonIndex >= 0) {
    const rawHours = rawValue.slice(0, colonIndex).replace(/\D/g, '');
    const rawMinutes = rawValue.slice(colonIndex + 1).replace(/\D/g, '');
    if (!rawHours) {
      return '';
    }

    if (rawHours.length === 1) {
      return `0${rawHours}:${normalizeMinutes(rawMinutes)}`;
    }

    const hours = rawHours.slice(0, 2);
    if (Number(hours) <= 23) {
      return `${hours}:${normalizeMinutes(rawMinutes)}`;
    }

    return `0${hours[0]}:${normalizeMinutes(
      `${hours[1]}${rawMinutes}`,
    )}`;
  }

  const digits = rawValue.replace(/\D/g, '');
  if (!digits) {
    return '';
  }
  if (digits.length === 1) {
    return Number(digits) > 2 ? `0${digits}:` : digits;
  }

  const hours = digits.slice(0, 2);
  if (Number(hours) <= 23) {
    return `${hours}:${normalizeMinutes(digits.slice(2))}`;
  }

  return `0${hours[0]}:${normalizeMinutes(digits.slice(1))}`;
}

export function normalizeSmartTimeInput(rawValue: string) {
  const formattedValue = formatSmartTimeInput(rawValue);
  if (!formattedValue) {
    return '';
  }
  if (!formattedValue.includes(':')) {
    return `${formattedValue.padStart(2, '0')}:00`;
  }

  const [hours, minutes = ''] = formattedValue.split(':');
  return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
}

const Input = styled('input')(({ theme }) => ({
  backgroundColor: 'transparent',
  border: 0,
  color: theme.palette.text.primary,
  fontFamily: 'inherit',
  fontSize: 14,
  height: 30,
  minWidth: 0,
  outline: 0,
  padding: 0,
  textAlign: 'center',
  width: '100%',
}));

type SmartTimeInputProps = Omit<
  ComponentPropsWithoutRef<'input'>,
  'defaultValue' | 'onBlur' | 'onChange' | 'type' | 'value'
> & {
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onValueChange?: (value: string) => void;
  value?: string;
};

const SmartTimeInput = forwardRef<HTMLInputElement, SmartTimeInputProps>(
  (
    {
      onBlur,
      onKeyDown,
      onValueChange,
      value = '',
      ...props
    },
    ref,
  ) => {
    const moveCaretToEnd = (
      input: HTMLInputElement,
      nextValue: string,
    ) => {
      window.requestAnimationFrame(() => {
        if (document.activeElement === input) {
          input.setSelectionRange(nextValue.length, nextValue.length);
        }
      });
    };

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
      onKeyDown?.(event);
      if (event.defaultPrevented || event.key !== 'Backspace') {
        return;
      }

      const input = event.currentTarget;
      const hasCollapsedSelection =
        input.selectionStart === input.selectionEnd;
      const caretIsAtEnd = input.selectionStart === value.length;
      if (
        !hasCollapsedSelection
        || !caretIsAtEnd
        || !value.endsWith(':')
      ) {
        return;
      }

      event.preventDefault();
      const nextValue = value.slice(0, -2);
      onValueChange?.(nextValue);
      moveCaretToEnd(input, nextValue);
    };

    return (
      <Input
        {...props}
        ref={ref}
        type="text"
        inputMode="numeric"
        maxLength={5}
        value={value}
        onChange={(event) => {
          const input = event.currentTarget;
          const wasEditingAtEnd =
            input.selectionStart === input.value.length;
          const nextValue = formatSmartTimeInput(input.value);
          onValueChange?.(nextValue);
          if (wasEditingAtEnd && nextValue !== input.value) {
            moveCaretToEnd(input, nextValue);
          }
        }}
        onBlur={(event) => {
          const normalizedValue = normalizeSmartTimeInput(value);
          if (normalizedValue !== value) {
            onValueChange?.(normalizedValue);
          }
          onBlur?.(event);
        }}
        onKeyDown={handleKeyDown}
      />
    );
  },
);

SmartTimeInput.displayName = 'SmartTimeInput';

export default SmartTimeInput;
